import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Input,
  Select,
  Text,
  Spinner,
  SimpleGrid,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { motion } from "framer-motion";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";

const CreateCompany = () => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [logo, setLogo] = useState(null); // File object
  const [profileImage1, setProfileImage1] = useState(null);
  const [profileImage2, setProfileImage2] = useState(null);
  const [profileImage3, setProfileImage3] = useState(null);
  const [serviceId, setServiceId] = useState("");
  const [userId, setUserId] = useState();
  const [ucleanCommission, setUcleanCommission] = useState(0);
  const [itemCommission, setItemCommission] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const logoInputRef = useRef(null);
  const profileImage1Ref = useRef(null);
  const profileImage2Ref = useRef(null);
  const profileImage3Ref = useRef(null);

  const [fieldErrors, setFieldErrors] = useState({});
  const [logoSize, setLogoSize] = useState(null);
  const [profileImage1Size, setProfileImage1Size] = useState(null);
  const [profileImage2Size, setProfileImage2Size] = useState(null);
  const [profileImage3Size, setProfileImage3Size] = useState(null);

  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const page = 11;

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetchWithToken(
          `${process.env.REACT_APP_API_URL}/services?page_id=${page}`
        );
        if (response) {
          setServices(response.data);
        }
      } catch (err) {
        console.error("Error fetching services:", err);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetchWithToken(
          `${process.env.REACT_APP_API_URL}/users?page_id=${page}`
        );
        if (response) {
          const filteredUsers = response.data.filter(user => user.role?.id === 3);
          setUsers(filteredUsers);
        }
      } catch (err) {
        console.error("Error fetching users:", err);
      }
    };
    fetchUsers();
  }, []);

  const validateImageSize = (file) => file.size <= 1024 * 1024;

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogoSize(file.size);
      if (!validateImageSize(file)) {
        setFieldErrors((prev) => ({ ...prev, logo: t("Image must be less than 1MB") }));
        setLogo(null);
        return;
      }
      setFieldErrors((prev) => ({ ...prev, logo: undefined }));
      setLogo(file);
    } else {
      setLogoSize(null);
      setLogo(null);
    }
  };

  const handleProfileImage1Upload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setProfileImage1Size(file.size);
      if (!validateImageSize(file)) {
        setFieldErrors((prev) => ({ ...prev, profileImage1: t("Image must be less than 1MB") }));
        setProfileImage1(null);
        return;
      }
      setFieldErrors((prev) => ({ ...prev, profileImage1: undefined }));
      setProfileImage1(file);
    } else {
      setProfileImage1Size(null);
      setProfileImage1(null);
    }
  };

  const handleProfileImage2Upload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setProfileImage2Size(file.size);
      if (!validateImageSize(file)) {
        setFieldErrors((prev) => ({ ...prev, profileImage2: t("Image must be less than 1MB") }));
        setProfileImage2(null);
        return;
      }
      setFieldErrors((prev) => ({ ...prev, profileImage2: undefined }));
      setProfileImage2(file);
    } else {
      setProfileImage2Size(null);
      setProfileImage2(null);
    }
  };

  const handleProfileImage3Upload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setProfileImage3Size(file.size);
      if (!validateImageSize(file)) {
        setFieldErrors((prev) => ({ ...prev, profileImage3: t("Image must be less than 1MB") }));
        setProfileImage3(null);
        return;
      }
      setFieldErrors((prev) => ({ ...prev, profileImage3: undefined }));
      setProfileImage3(file);
    } else {
      setProfileImage3Size(null);
      setProfileImage3(null);
    }
  };

  const handleSubmit = async (event) => {
    event && event.preventDefault();

    if (!profileImage1 || !profileImage2 || !profileImage3) {
      setError(t("All 3 profile images are required"));
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("name", name);
    formData.append("service_id", parseInt(serviceId));
    formData.append("manager_id", parseInt(userId));
    if (logo) formData.append("logo", logo);
    formData.append("uclean_commission", Number(parseFloat(ucleanCommission).toFixed(2)));
    formData.append("item_commission", Number(parseFloat(itemCommission).toFixed(2)));
    formData.append("profile_images[profile_image_1]", profileImage1);
    formData.append("profile_images[profile_image_2]", profileImage2);
    formData.append("profile_images[profile_image_3]", profileImage3);

    try {
      await fetchWithToken(
        `${process.env.REACT_APP_API_URL2}/company/create?page_id=${page}`,
        "POST",
        formData
        // Do NOT set Content-Type header; browser will set it for FormData
      );
      setName("");
      setLogo(null);
      setProfileImage1(null);
      setProfileImage2(null);
      setProfileImage3(null);
      setServiceId("");
      setUserId(undefined);
      setUcleanCommission(0);
      setItemCommission(0);
      if (logoInputRef.current) logoInputRef.current.value = "";
      if (profileImage1Ref.current) profileImage1Ref.current.value = "";
      if (profileImage2Ref.current) profileImage2Ref.current.value = "";
      if (profileImage3Ref.current) profileImage3Ref.current.value = "";
      setError("");
      setSuccess(true);
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      setError(err.message || "An error occurred. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper for previewing File objects
  const getImagePreview = (file) => {
    if (!file) return null;
    if (typeof file === "string") return file;
    return URL.createObjectURL(file);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.5 }}
    >
      <Card p={{ base: "20px", md: "30px" }}>
        <form onSubmit={handleSubmit}>
          {error && (
            <Text color="#FF3B3B" textAlign="center" fontSize="lg">
              {error}
            </Text>
          )}
          {success && (
            <Text color="green.500" textAlign="center">
              {t("Company created successfully!")}
            </Text>
          )}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {/* Name */}
            <FormControl isRequired isInvalid={!!fieldErrors.name}>
              <FormLabel color="blue.600">{t("Name")}</FormLabel>
              <Input
                placeholder="Name of the company"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <FormErrorMessage>{fieldErrors.name}</FormErrorMessage>
            </FormControl>
            {/* Logo Upload */}
            <FormControl isInvalid={!!fieldErrors.logo}>
              <FormLabel color="blue.600">{t("Logo")}</FormLabel>
              <Input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                ref={logoInputRef}
              />
              {logoSize !== null && (
                <Text fontSize="xs" color={logoSize > 1024 * 1024 ? "red.500" : "gray.500"} mt={1}>
                  {t("Selected file size")}: {(logoSize / 1024).toFixed(1)} KB
                  {logoSize > 1024 * 1024 && ` (${t("Too large")})`}
                </Text>
              )}
              <FormErrorMessage>{fieldErrors.logo}</FormErrorMessage>
            </FormControl>
            {/* Service Select */}
            <FormControl isRequired isInvalid={!!fieldErrors.serviceId}>
              <FormLabel color="blue.600">{t("Service")}</FormLabel>
              <Select
                required
                value={serviceId}
                onChange={(e) => setServiceId(e.target.value)}
                placeholder={t("Select a service")}
              >
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{fieldErrors.serviceId}</FormErrorMessage>
            </FormControl>
            {/* User Select */}
            <FormControl isRequired isInvalid={!!fieldErrors.userId}>
              <FormLabel color="blue.600">{t("User")}</FormLabel>
              <Select
                required
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder={t("Select a user")}
              >
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.role.name})
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{fieldErrors.userId}</FormErrorMessage>
            </FormControl>
            {/* Uclean Commission */}
            <FormControl isRequired isInvalid={!!fieldErrors.ucleanCommission}>
              <FormLabel color="blue.600">{t("Ucleany Commission %")}</FormLabel>
              <Input
                min={0}
                max={100}
                type="number"
                step={0.01}
                value={ucleanCommission}
                onChange={(e) => setUcleanCommission(e.target.value)}
                required
              />
              <FormErrorMessage>{fieldErrors.ucleanCommission}</FormErrorMessage>
            </FormControl>
            {/* Item Commission */}
            <FormControl isRequired isInvalid={!!fieldErrors.itemCommission}>
              <FormLabel color="blue.600">{t("Item Commission %")}</FormLabel>
              <Input
                min={0}
                max={100}
                type="number"
                step={0.01}
                value={itemCommission}
                onChange={(e) => setItemCommission(e.target.value)}
                required
              />
              <FormErrorMessage>{fieldErrors.itemCommission}</FormErrorMessage>
            </FormControl>
            {/* Profile Image 1 */}
            <FormControl isRequired isInvalid={!!fieldErrors.profileImage1}>
              <FormLabel color="blue.600">{t("Profile Image 1")}</FormLabel>
              <Input
                type="file"
                accept="image/*"
                onChange={handleProfileImage1Upload}
                ref={profileImage1Ref}
                required
              />
              {profileImage1Size !== null && (
                <Text fontSize="xs" color={profileImage1Size > 1024 * 1024 ? "red.500" : "gray.500"} mt={1}>
                  {t("Selected file size")}: {(profileImage1Size / 1024).toFixed(1)} KB
                  {profileImage1Size > 1024 * 1024 && ` (${t("Too large")})`}
                </Text>
              )}
              <FormErrorMessage>{fieldErrors.profileImage1}</FormErrorMessage>
            </FormControl>
            {/* Profile Image 2 */}
            <FormControl isRequired isInvalid={!!fieldErrors.profileImage2}>
              <FormLabel color="blue.600">{t("Profile Image 2")}</FormLabel>
              <Input
                type="file"
                accept="image/*"
                onChange={handleProfileImage2Upload}
                ref={profileImage2Ref}
                required
              />
              {profileImage2Size !== null && (
                <Text fontSize="xs" color={profileImage2Size > 1024 * 1024 ? "red.500" : "gray.500"} mt={1}>
                  {t("Selected file size")}: {(profileImage2Size / 1024).toFixed(1)} KB
                  {profileImage2Size > 1024 * 1024 && ` (${t("Too large")})`}
                </Text>
              )}
              <FormErrorMessage>{fieldErrors.profileImage2}</FormErrorMessage>
            </FormControl>
            {/* Profile Image 3 */}
            <FormControl isRequired isInvalid={!!fieldErrors.profileImage3}>
              <FormLabel color="blue.600">{t("Profile Image 3")}</FormLabel>
              <Input
                type="file"
                accept="image/*"
                onChange={handleProfileImage3Upload}
                ref={profileImage3Ref}
                required
              />
              {profileImage3Size !== null && (
                <Text fontSize="xs" color={profileImage3Size > 1024 * 1024 ? "red.500" : "gray.500"} mt={1}>
                  {t("Selected file size")}: {(profileImage3Size / 1024).toFixed(1)} KB
                  {profileImage3Size > 1024 * 1024 && ` (${t("Too large")})`}
                </Text>
              )}
              <FormErrorMessage>{fieldErrors.profileImage3}</FormErrorMessage>
            </FormControl>
          </SimpleGrid>
          <Box>
            <Button
              mt={4}
              colorScheme="cyan"
              fontSize="m"
              borderRadius="16px"
              w={{ base: "100%", md: "148px" }}
              h="46px"
              type="submit"
              color="white"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Spinner size="sm" /> : t("Submit")}
            </Button>
          </Box>

          {/* Show all selected images with labels at the bottom */}
          <Box mt={8}>
            <Text fontWeight="bold" mb={2}>{t("Selected Images")}</Text>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
              {logo && (
                <Box textAlign="center">
                  <Text fontSize="sm" mb={1}>{t("Logo")}</Text>
                  <img src={getImagePreview(logo)} alt="Logo Preview" style={{ maxWidth: "100%", maxHeight: "120px", borderRadius: "8px" }} />
                </Box>
              )}
              {profileImage1 && (
                <Box textAlign="center">
                  <Text fontSize="sm" mb={1}>{t("Profile Image 1")}</Text>
                  <img src={getImagePreview(profileImage1)} alt="Profile 1 Preview" style={{ maxWidth: "100%", maxHeight: "120px", borderRadius: "8px" }} />
                </Box>
              )}
              {profileImage2 && (
                <Box textAlign="center">
                  <Text fontSize="sm" mb={1}>{t("Profile Image 2")}</Text>
                  <img src={getImagePreview(profileImage2)} alt="Profile 2 Preview" style={{ maxWidth: "100%", maxHeight: "120px", borderRadius: "8px" }} />
                </Box>
              )}
              {profileImage3 && (
                <Box textAlign="center">
                  <Text fontSize="sm" mb={1}>{t("Profile Image 3")}</Text>
                  <img src={getImagePreview(profileImage3)} alt="Profile 3 Preview" style={{ maxWidth: "100%", maxHeight: "120px", borderRadius: "8px" }} />
                </Box>
              )}
            </SimpleGrid>
          </Box>
        </form>
      </Card>
    </motion.div>
  );
};

export default CreateCompany;